// Remembers which session tokens have already accepted the Terms & Conditions
// gate, so a subject who re-enters via a reused or reminder `?s=` link (see
// `composables/dataLoader.ts`) isn't re-prompted. Client-side only — no CORE
// API involvement, per the executive request that motivated this.

const COOKIE_NAME = 'cady_terms_accepted_tokens'
const MAX_AGE_DAYS = 90
const MAX_ENTRIES = 20

interface AcceptedEntry {
  t: string // session token
  a: string // ISO acceptance timestamp
}

function readEntries(): AcceptedEntry[] {
  if (typeof document === 'undefined') return []

  const match = document.cookie.split('; ').find((row) => row.startsWith(`${COOKIE_NAME}=`))
  if (!match) return []

  let entries: AcceptedEntry[]
  try {
    entries = JSON.parse(decodeURIComponent(match.slice(COOKIE_NAME.length + 1)))
  } catch {
    return []
  }
  if (!Array.isArray(entries)) return []

  const cutoff = Date.now() - MAX_AGE_DAYS * 24 * 60 * 60 * 1000
  return entries.filter(
    (entry) =>
      entry &&
      typeof entry.t === 'string' &&
      typeof entry.a === 'string' &&
      Date.parse(entry.a) >= cutoff
  )
}

function writeEntries(entries: AcceptedEntry[]): void {
  if (typeof document === 'undefined') return

  const value = encodeURIComponent(JSON.stringify(entries))
  const maxAgeSeconds = MAX_AGE_DAYS * 24 * 60 * 60
  const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : ''
  document.cookie = `${COOKIE_NAME}=${value}; Max-Age=${maxAgeSeconds}; Path=/; SameSite=Lax${secure}`
}

export function hasAcceptedTermsToken(token: string): boolean {
  return readEntries().some((entry) => entry.t === token)
}

export function forgetTermsAcceptance(token: string): void {
  writeEntries(readEntries().filter((entry) => entry.t !== token))
}

export function rememberTermsAcceptance(token: string): void {
  const entries = readEntries().filter((entry) => entry.t !== token)
  entries.push({ t: token, a: new Date().toISOString() })
  entries.sort((a, b) => Date.parse(b.a) - Date.parse(a.a))
  writeEntries(entries.slice(0, MAX_ENTRIES))
}
